// Breakpoints
type BreakpointKeys = 'sm' | 'md' | 'lg' | 'xl' | '2xl';
export type Breakpoints = Record<BreakpointKeys, string>;
const breakpoints: Breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Font Scales
const fontScales = {
  xs: ['1.2rem', '1.9rem'],
  s: ['1.4rem', '2.3rem'],
  base: ['1.6rem', '2.4rem'],
  lg: ['1.8rem', '2.9rem'],
  xl: ['2.4rem', '3.8rem'],
  xl1: ['2.5rem', '4rem'],
  xl2: ['2.8rem', '4.5rem'],
  xl3: ['3rem', '4.8rem'],
  xl4: ['4.8rem', '7.8rem'],
  xl5: ['6rem', '10.1rem'],
} as const;
export type FontScales = typeof fontScales;

// Font Weights
const fontWeights = {
  extraLight: '300',
  light: '400',
  normal: '500',
  bold: '600',
  extraBold: '700',
} as const;

export type FontWeights = typeof fontWeights;

const textTransforms = {
  uppercase: 'uppercase',
  capitalize: 'capitalize',
  lowercase: 'lowercase',
  none: 'none',
  isFirstLetterUppercase: 'isFirstLetterUppercase',
} as const;

export type TextTransforms = typeof textTransforms;

const textAligns = {
  center: 'center',
  left: 'left',
  right: 'right',
  justify: 'justify',
} as const;

export type TextAligns = typeof textAligns;

// Colors
const colors = {
  inherit: 'inherit',

  white: '#ffffff',
  whiteDark: '#f5f5f5',
  transparentWhiteBackground: 'rgba(255, 255, 255, 0.85)',

  gray: '#eeeeee',
  grayLight: '#e3e3e3',
  grayLightSecondary: '#fffdfc',
  grayDark: '#555555',
  grayMain: '#232322',
  grayDarkSecondary: '#777777',
  buttonGray: '#ededee',
  grayLabel: '#6e6e6e',

  silver: '#c0c0c0',

  borderExtraLight: '#dddddd',
  borderLight: '#cccccc',
  borderLightSecondary: '#626b77',
  borderRed: '#d43f3a',
  borderDisabled: '#e6e6e6',
  borderSemiLight: '#e5e5e5',
  borderTransparent: 'rgba(255, 255, 255, 0.1)',

  backButtonShadow: 'rgba(99, 99, 99, 0.2) 0 0.4rem 0.8rem 0',

  title: '#444444',

  black: '#000000',
  blackLight: '#333333',
  blackTransparent: 'rgba(0, 0, 0, 0.3)',
  blackTransparentSecondary: 'rgba(0, 0, 0, 0.6)',
  blackTransparentTertiary: 'rgba(0, 0, 0, 0.05)',

  red: '#ff0000',
  redSecondary: '#c7254e',
  redLight: '#d9534f',
  redLightSecondary: '#ff9999',
  colmarRed: '#AA182C',
  darkRed: '#960023',
  darkRedSecondary: '#550E35',
  dangerRed: '#a94442',
  dangerRedLight: '#ebccd1',

  blue: '#337ab7',
  blueDark: '#286090',
  blueGray: '#5f778c',
  colmarBlue: '#004A99',
  blueTransparent: 'rgba(0,74,153,.30)',
  blueFilter: '#C6D8EC',

  saveOrange: '#cf7b27',
  orange: '#eb9a49',

  yellowWarning: '#FFCE31',

  green: '#5cb85c',
  greenLight: '#dff0d8',
  greenLightSecondary: '#59ba41',
  greenDark: '#184b21',
  greenStatus: '#008000',
  greenSecondary: '#009933',

  rowBackground: '#f2f2f2',
  shadow: 'rgba(66, 68, 90, 1)',
  restTabText: '#5A5F64',

  borderColorDark: '#cfcfcf',
  borderColorDarkSecondary: '#5A5F64',

  accepted: '#DFFAE6',
  acceptedFont: '#4F7767',
  inProgress: '#FFF5CC',
  inProgressFont: '#B57803',
  inReview: '#DAF3FF',
  inReviewFont: '#086898',
  refused: '#E2CBFF',
  refusedFont: '#500CA4',
  abandoned: '#FFBCBC',
  abandonedFont: '#A40C0C',
  abandonedBackground: '#FFEFF3',
  new: '#006EE3',
  deleteRequested: '#FFE0CC',
  deleteRequestedFont: '#B54708',

  disabled: '#E2E2E2',

  message: 'rgba(90, 95, 100, 0.1)',
  messageSecondary: ' rgba(0, 74, 153, 0.1)',

  warning: '#fcf8e3',
  warningBorder: '#faebcc',
  warningText: '#8a6d3b',
} as const;

export type Colors = typeof colors;

export { breakpoints, fontScales, fontWeights, textTransforms, textAligns, colors };
